import {
  Box,
  Card,
  Dialog,
  Grid,
  Stack,
  Typography,
  Alert,
  Chip,
  LinearProgress,
  Button,
  IconButton,
  TextField,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import { useTranslate, useNotify, useRefresh } from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getRewardPoolBalances,
  setRewardPoolBudget,
  RewardPoolBalance,
  PoolType,
  SetRewardPoolBudgetInput,
  PostCampaignCleanupStats,
  getPostCampaignCleanupStats,
} from '../../../services/redpacketService';
import { fNumber } from '../../../../../utils/format-number';
import moment from 'moment/moment';

const RewardPoolBalancesList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [balances, setBalances] = useState<RewardPoolBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<RewardPoolBalance | null>(null);
  const [budgetValue, setBudgetValue] = useState('');
  const [saving, setSaving] = useState(false);
  const refresh = useRefresh();
  const [cleanupStats, setCleanupStats] = useState<PostCampaignCleanupStats | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getRewardPoolBalances();
      setBalances(result);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setBalances([]);
    } finally {
      setLoading(false);
    }
  }, [notify,translate]);

  useEffect(() => {
    fetchData();
    refresh();
  }, [fetchData, refresh]);

  // 初始加载：获取活动结束后的清理统计
  useEffect(() => {
    const fetchCleanupStats = async () => {
      try {
        const cleanupStatsResult = await getPostCampaignCleanupStats();
        setCleanupStats(cleanupStatsResult);
      } catch (error) {
        console.error('Failed to fetch cleanup stats:', error);
      }
    };
    fetchCleanupStats();
  }, []);

  const handleEditClick = (pool: RewardPoolBalance) => {
    setEditingPool(pool);
    setBudgetValue(pool.totalBudget);
    setEditDialogOpen(true);
  };

  const handleSaveBudget = async () => {
    if (!editingPool || !budgetValue) {
      notify(translate('module.redpacket.pleaseEnterValidBudget'), { type: 'error' });
      return;
    }

    try {
      setSaving(true);
      const input: SetRewardPoolBudgetInput = {
        poolType: editingPool.poolType,
        totalBudget: budgetValue,
      };
      await setRewardPoolBudget(input);
      notify(translate('module.redpacket.budgetUpdatedSuccess'), { type: 'success' });
      setEditDialogOpen(false);
      setEditingPool(null);
      setBudgetValue('');
      fetchData();
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.budgetUpdateFailed'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getPoolTypeLabel = (poolType: PoolType) => {
    switch (poolType) {
      case PoolType.SHARED:
        return translate('module.redpacket.sharedPoolName');
      case PoolType.CRITICAL_HIT:
        return translate('module.redpacket.criticalHitPoolName');
      case PoolType.CRITICAL_HIT_DESIGNATED:
        return translate('module.redpacket.criticalHitDesignatedPoolName');
      default:
        return poolType;
    }
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.rewardPoolBalances')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.rewardPoolBalances') },
          ]}
        />

        <Card sx={{ mt: 3, p: 3 }}>
          {loading ? (
            <LinearProgress />
          ) : balances.length === 0 ? (
            <Alert severity="info">No reward pool balances found</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('module.redpacket.poolType')}</TableCell>
                    <TableCell align="right">{translate('module.redpacket.totalBudgetCol')}</TableCell>
                    <TableCell align="right">{translate('module.redpacket.availableBalance')}</TableCell>
                    <TableCell align="right">{translate('module.redpacket.spentBalance')}</TableCell>
                    <TableCell align="right">{translate('module.redpacket.frozenBalance')}</TableCell>
                    {cleanupStats?.latestRun && (
                      <TableCell align="right">{translate('module.redpacket.recovered')}</TableCell>
                    )}
                    <TableCell align="right">Spent %</TableCell>
                    <TableCell align="right">Remaining %</TableCell>
                    <TableCell>{translate('module.redpacket.updatedAtCol')}</TableCell>
                    <TableCell align="center">{translate('module.redpacket.action')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balances.map((pool) => (
                    <TableRow key={pool.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {getPoolTypeLabel(pool.poolType)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          ${fNumber(parseFloat(pool.totalBudget || '0'))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          ${fNumber(parseFloat(pool.availableBalance || '0'))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main">
                          ${fNumber(parseFloat(pool.spentBalance || '0'))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          ${fNumber(parseFloat(pool.frozenBalance || '0'))}
                        </Typography>
                      </TableCell>
                      {cleanupStats?.latestRun && (
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main">
                            ${pool.poolType === PoolType.SHARED && (cleanupStats.latestRun.sharedPoolRecovered || '0')}
                           {pool.poolType === PoolType.CRITICAL_HIT &&  (cleanupStats.latestRun.criticalHitPoolRecovered || '0')}
                           {pool.poolType === PoolType.CRITICAL_HIT_DESIGNATED &&  (cleanupStats.latestRun.designatedPoolRecovered || '0')}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Chip
                          label={`${pool.spentPercentage}%`}
                          color={
                            parseFloat(pool.spentPercentage) > 80
                              ? 'error'
                              : parseFloat(pool.spentPercentage) > 50
                                ? 'warning'
                                : 'success'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${pool.remainingPercentage}%`}
                          color={
                            parseFloat(pool.remainingPercentage) < 20
                              ? 'error'
                              : parseFloat(pool.remainingPercentage) < 50
                                ? 'warning'
                                : 'success'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {moment(pool.updatedAt).format('YYYY-MM-DD HH:mm:ss')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <PermissionButton resource="redpacket" action="update">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditClick(pool)}
                          >
                            <EditIcon />
                          </IconButton>
                        </PermissionButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{translate('module.redpacket.setRewardPoolBudget')}</DialogTitle>
          <DialogContent>
            {editingPool && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Pool Type: <strong>{getPoolTypeLabel(editingPool.poolType)}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate('module.redpacket.currentBudget')}:{' '}
                  <strong>${fNumber(parseFloat(editingPool.totalBudget || '0'))}</strong>
                </Typography>
                <TextField
                  label={translate('module.redpacket.newTotalBudget')}
                  type="text"
                  value={budgetValue}
                  onChange={(e) => setBudgetValue(e.target.value)}
                  fullWidth
                  required
                  helperText={translate('module.redpacket.enterNewBudgetHelper')}
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
              {translate('actions.cancel')}
            </Button>
            <Button
              onClick={handleSaveBudget}
              variant="contained"
              disabled={saving || !budgetValue}
            >
              {saving ? translate('module.redpacket.saving') : translate('actions.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {popup && popup}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default RewardPoolBalancesList;
